import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { BaseDashboardFiltersComponent } from '../base-dashboard-filters.component';
import { GaValidateService } from '../../../../../shared/formly/ga-validate.service';
import { GaMessageService } from '../../../../../shared/message/ga-message.service';
import { TranslationService } from '../../../../../shared/translation/translation.service';
import { ReportDataService } from '../../../../../shared/report/report-data.service';
import { ReportFiltersUtil } from '../../../../../shared/report/report-filters-util.service';
import { DashboardService } from '../../dashboard.service';
import { GaDeferredService } from '../../../../../shared/services/ga-deferred.service';
import { AuthService } from '../../../../../shared/services/auth/auth.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../../shared/formly/ga-formly.service';
import { Override } from '../../../../../shared/app-decorators';
import { FormattingService } from '../../../../../shared/services/formatting.service';
import { WebshopDataService } from '../../../../webshop/webshop-data.service';
import { CountryDataService } from '../../../../../shared/location/country-data.service';
import { DashboardDataService } from '../../../../../shared/report/dashboard-data.service';
import { CompanyDataService } from '../../../../../shared/company/company-data.service';
import { ContactDataService } from '../../../../../shared/contact/contact-data.service';

@Component({
  selector: 'oem-dashboard-filters',
  templateUrl: '../base-dashboard-filters.component.html'
})
export class OemDashboardFiltersComponent extends BaseDashboardFiltersComponent implements OnDestroy {
  @Input() public showIntelFields:boolean;

  private countryField:IExtraFieldConfigurationObject;
  private customerField:IExtraFieldConfigurationObject;
  private oemField:IExtraFieldConfigurationObject;
  private vnamField:IExtraFieldConfigurationObject;
  private bpField:IExtraFieldConfigurationObject;
  private geographyField:IExtraFieldConfigurationObject;
  private armField:IExtraFieldConfigurationObject;
  private gbdmOEMField:IExtraFieldConfigurationObject;
  private pssField:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private webshopDataService:WebshopDataService,
    private countryDataService:CountryDataService,
    private dashboardDataService:DashboardDataService,
    private companyDataService:CompanyDataService,
    private contactDataService:ContactDataService,
    validateService:GaValidateService,
    messageService:GaMessageService,
    translationService:TranslationService,
    reportDataService:ReportDataService,
    reportFiltersUtil:ReportFiltersUtil,
    dashboardService:DashboardService,
    deferredService:GaDeferredService,
    authService:AuthService,
    cdr:ChangeDetectorRef
  ) {
    super(validateService, messageService, translationService, reportDataService, reportFiltersUtil, dashboardService, deferredService, authService, cdr);
  }

  protected customValidatorFn = ():Promise<void | string[]> => {
    if (!this.model.customer && !this.model.bp && !this.model.geography && !this.model.oem && !this.model.vnam && !this.model.country) {
      return Promise.reject('Please select at least one of the following filters: Customer, BP, Geography, OEM, VNAM, Country.');
    }

    return Promise.resolve();
  };

  @Override(BaseDashboardFiltersComponent)
  public fillFields():void {
    const promises:Array<Promise<any>> = [
      this.formlyService.fillField(this.customerField, this.webshopDataService.webshops()),
      this.formlyService.fillField(this.countryField, this.countryDataService.getAllCountries()),
      this.formlyService.fillField(this.bpField, this.dashboardDataService.BPs()),
      this.formlyService.fillField(this.geographyField, this.countryDataService.listContinents()),
      this.formlyService.fillField(this.oemField, this.companyDataService.getVendorCompanies()),
      this.formlyService.fillField(this.vnamField, this.contactDataService.NAMs('VNAM'))
    ];

    if (this.showIntelFields) {
      promises.push(
        this.formlyService.fillField(this.armField, this.dashboardDataService.gbdms())
      );
      promises.push(
        this.formlyService.fillField(this.gbdmOEMField, this.dashboardDataService.gbdms())
      );
      promises.push(
        this.formlyService.fillField(this.pssField, this.dashboardDataService.ODs())
      );
    } else {
      // check if current user is acsl user
      if (this.authService.checkAcslUser()) {
        promises.push(
          this.formlyService.fillField(this.armField, this.dashboardDataService.gbdms())
        );
      }
    }

    Promise.all(promises)
      .then(this.setQueryFilters);
  }

  @Override(BaseDashboardFiltersComponent)
  public createFields():IExtraFieldConfigurationObject[] {
    return [
      this.customerField = this.formlyService.aSearch('customer', 'Webshop')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatName)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.vnamField = this.formlyService.aSearch('vnam', 'VNAM')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatContact)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.oemField = this.formlyService.aSearch('oem', 'OEM')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCompany)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.bpField = this.formlyService.aSearch('bp', 'BP')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatBPNicknameCountry)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.geographyField = this.formlyService.aSearch('geography', 'Geography')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatName)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.countryField = this.formlyService.aSelect('country', 'Country')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCountry)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.armField = this.formlyService.aSelect('arm', 'ARM')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatContact)
        .withHideExpression(() => !this.showIntelFields && !this.isACSLUser)
        .build(),

      this.gbdmOEMField = this.formlyService.aSelect('gbdmOEM', 'GBDM OEM')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatContact)
        .withHideExpression(() => !this.showIntelFields)
        .build(),

      this.pssField = this.formlyService.aSelect('pss', 'PSS')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatContact)
        .withHideExpression(() => !this.showIntelFields)
        .build()
    ];
  }

  public ngOnDestroy():void {
    this.dashboardDataService.clear();
  }
}